import { useCallback, useId, useRef, useState } from 'react';
import { PdfView, boxStyle } from './PdfView';
import type { PageSize } from './PdfView';
import { Banner } from './ui';

export type EditorField = {
  id?: string;
  localId: string;
  kind: 'signature' | 'initials' | 'date' | 'text' | 'checkbox' | 'radio';
  label: string;
  page: number;
  x: number; y: number; width: number; height: number;
  required: boolean;
  recipient_id?: string | null;
  role_key?: string | null;
  group_key?: string | null;
  options?: { value: string; label: string }[];
};

const TOOLS: { kind: EditorField['kind']; icon: string; label: string;
               w: number; h: number }[] = [
  { kind: 'signature', icon: '✍', label: 'Signature', w: 190, h: 46 },
  { kind: 'initials', icon: 'AB', label: 'Initials', w: 72, h: 40 },
  { kind: 'date', icon: '📅', label: 'Date', w: 130, h: 28 },
  { kind: 'text', icon: 'T', label: 'Text', w: 190, h: 28 },
  { kind: 'checkbox', icon: '☑', label: 'Checkbox', w: 22, h: 22 },
];

const MIN_W = 18;
const MIN_H = 12;

let counter = 0;
const nextLocalId = () => `local-${Date.now().toString(36)}-${counter++}`;

type Assignee = { id: string; name: string; color?: string };

/**
 * Touch-and-mouse field editor.
 *
 * Pointer events handle mouse, pen and touch with one code path. Geometry is
 * kept in PDF points throughout; the only conversion is to percentages at paint
 * time, so what you place on a phone matches what a desktop signer sees.
 */
export function FieldEditor({
  url, pageSizes, fields, onChange, assignees = [], activeAssignee, onActiveAssignee,
  headers,
}: {
  url: string;
  pageSizes: PageSize[];
  fields: EditorField[];
  onChange: (fields: EditorField[]) => void;
  assignees?: Assignee[];
  activeAssignee?: string | null;
  onActiveAssignee?: (id: string) => void;
  /** Sent with the document request, for embedded sessions. */
  headers?: Record<string, string>;
}) {
  const [tool, setTool] = useState<EditorField['kind'] | null>('signature');
  const [selected, setSelected] = useState<string | null>(null);
  const drag = useRef<{
    localId: string; mode: 'move' | 'resize';
    startX: number; startY: number;
    originX: number; originY: number; originW: number; originH: number;
    pxPerPoint: number;
  } | null>(null);
  const hintId = useId();

  const placeField = useCallback((pageIndex: number, size: PageSize,
                                  event: React.MouseEvent<HTMLDivElement>) => {
    if (!tool) return;
    const target = event.currentTarget.getBoundingClientRect();
    const spec = TOOLS.find((t) => t.kind === tool)!;
    // Drop centred on the tap point, clamped inside the page.
    const relX = ((event.clientX - target.left) / target.width) * size.width;
    const relY = ((target.bottom - event.clientY) / target.height) * size.height;
    const x = Math.max(0, Math.min(relX - spec.w / 2, size.width - spec.w));
    const y = Math.max(0, Math.min(relY - spec.h / 2, size.height - spec.h));

    const field: EditorField = {
      localId: nextLocalId(),
      kind: spec.kind,
      label: spec.label === 'Date' ? 'Date signed' : spec.label,
      page: pageIndex,
      x: Math.round(x), y: Math.round(y), width: spec.w, height: spec.h,
      required: spec.kind !== 'text',
      recipient_id: activeAssignee || assignees[0]?.id || null,
    };
    onChange([...fields, field]);
    setSelected(field.localId);
  }, [tool, fields, onChange, activeAssignee, assignees]);

  const beginDrag = useCallback((
    event: React.PointerEvent, field: EditorField, mode: 'move' | 'resize', size: PageSize,
  ) => {
    event.stopPropagation();
    event.preventDefault();
    const page = (event.currentTarget as HTMLElement).closest('.pdfpage');
    if (!page) return;
    const rect = page.getBoundingClientRect();
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    drag.current = {
      localId: field.localId, mode,
      startX: event.clientX, startY: event.clientY,
      originX: field.x, originY: field.y, originW: field.width, originH: field.height,
      pxPerPoint: rect.width / size.width,
    };
    setSelected(field.localId);
  }, []);

  const onDragMove = useCallback((event: React.PointerEvent, size: PageSize) => {
    const state = drag.current;
    if (!state) return;
    event.preventDefault();
    const dx = (event.clientX - state.startX) / state.pxPerPoint;
    // Screen Y grows downward, PDF Y grows upward.
    const dy = -(event.clientY - state.startY) / state.pxPerPoint;

    onChange(fields.map((field) => {
      if (field.localId !== state.localId) return field;
      if (state.mode === 'move') {
        return {
          ...field,
          x: Math.round(Math.max(0, Math.min(state.originX + dx, size.width - field.width))),
          y: Math.round(Math.max(0, Math.min(state.originY + dy, size.height - field.height))),
        };
      }
      const width = Math.max(MIN_W, Math.min(state.originW + dx, size.width - field.x));
      const height = Math.max(MIN_H, Math.min(state.originH - dy, state.originY + state.originH));
      return {
        ...field,
        width: Math.round(width),
        height: Math.round(height),
        y: Math.round(state.originY + state.originH - height),
      };
    }));
  }, [fields, onChange]);

  const endDrag = useCallback(() => { drag.current = null; }, []);

  const removeField = useCallback((localId: string) => {
    onChange(fields.filter((f) => f.localId !== localId));
    setSelected((current) => (current === localId ? null : current));
  }, [fields, onChange]);

  /** Keyboard nudging keeps the editor usable without a pointer. */
  const onKeyDown = useCallback((event: React.KeyboardEvent, field: EditorField,
                                 size: PageSize) => {
    const step = event.shiftKey ? 10 : 2;
    const moves: Record<string, [number, number]> = {
      ArrowLeft: [-step, 0], ArrowRight: [step, 0],
      ArrowUp: [0, step], ArrowDown: [0, -step],
    };
    if (moves[event.key]) {
      event.preventDefault();
      const [dx, dy] = moves[event.key];
      onChange(fields.map((f) => f.localId !== field.localId ? f : {
        ...f,
        x: Math.max(0, Math.min(f.x + dx, size.width - f.width)),
        y: Math.max(0, Math.min(f.y + dy, size.height - f.height)),
      }));
    } else if (event.key === 'Delete' || event.key === 'Backspace') {
      event.preventDefault();
      removeField(field.localId);
    }
  }, [fields, onChange, removeField]);

  const assigneeName = (id?: string | null) =>
    assignees.find((a) => a.id === id)?.name;

  return (
    <div className="editor">
      <div className="editor__side">
        <div className="card card--flat stack-s" style={{ padding: 14 }}>
          <h3 className="card__title" style={{ fontSize: '.88rem' }}>Add a field</h3>
          <div className="tool-grid" role="group" aria-label="Field types">
            {TOOLS.map((spec) => (
              <button
                key={spec.kind}
                type="button"
                className="tool"
                aria-pressed={tool === spec.kind}
                onClick={() => setTool(tool === spec.kind ? null : spec.kind)}
              >
                <span className="tool__icon" aria-hidden="true">{spec.icon}</span>
                {spec.label}
              </button>
            ))}
          </div>
          <p className="small muted" id={hintId} style={{ margin: 0 }}>
            {tool
              ? 'Tap the page to place the field. Drag to move, use the corner to resize.'
              : 'Pick a field type, then tap the page to place it.'}
          </p>
        </div>

        {assignees.length > 1 && (
          <div className="card card--flat stack-s" style={{ padding: 14 }}>
            <h3 className="card__title" style={{ fontSize: '.88rem' }}>Assign new fields to</h3>
            <div className="switch-list">
              {assignees.map((person) => (
                <label className="radio" key={person.id}>
                  <input
                    type="radio"
                    name="assignee"
                    checked={(activeAssignee || assignees[0]?.id) === person.id}
                    onChange={() => onActiveAssignee?.(person.id)}
                  />
                  <span className="checkbox__text">{person.name}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="card card--flat stack-s" style={{ padding: 14 }}>
          <h3 className="card__title" style={{ fontSize: '.88rem' }}>
            Placed fields ({fields.length})
          </h3>
          {fields.length === 0 ? (
            <p className="small muted" style={{ margin: 0 }}>No fields yet.</p>
          ) : (
            <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 6 }}>
              {fields.map((field) => (
                <li key={field.localId}>
                  <button
                    type="button"
                    className="btn btn--small btn--block"
                    style={{
                      justifyContent: 'space-between',
                      borderColor: selected === field.localId
                        ? 'var(--brand)' : 'var(--line)',
                    }}
                    onClick={() => setSelected(field.localId)}
                  >
                    <span className="truncate">{field.label}</span>
                    <span className="muted" style={{ fontSize: '.74rem' }}>
                      p{field.page + 1}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div>
        {fields.length === 0 && (
          <div style={{ marginBottom: 12 }}>
            <Banner tone="info" title="Place at least one signature field">
              Every signer needs a signature or initials field before the agreement
              can be sent.
            </Banner>
          </div>
        )}
        <PdfView
          url={url}
          headers={headers}
          pageSizes={pageSizes}
          renderOverlay={(pageIndex, size) => (
            <div
              style={{ position: 'absolute', inset: 0, cursor: tool ? 'crosshair' : 'default' }}
              onClick={(event) => placeField(pageIndex, size, event)}
              onPointerMove={(event) => onDragMove(event, size)}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              {fields.filter((field) => field.page === pageIndex).map((field) => {
                const owner = assigneeName(field.recipient_id);
                return (
                  <div
                    key={field.localId}
                    className={`fieldbox${selected === field.localId ? ' fieldbox--selected' : ''}`}
                    style={boxStyle(field, size)}
                    onPointerDown={(event) => beginDrag(event, field, 'move', size)}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => onKeyDown(event, field, size)}
                    tabIndex={0}
                    role="button"
                    aria-label={`${field.label}${owner ? ` for ${owner}` : ''}, page ${pageIndex + 1}. ` +
                                'Use arrow keys to move, Delete to remove.'}
                  >
                    <span className="fieldbox__value">
                      {field.label}{assignees.length > 1 && owner ? ` · ${owner.split(' ')[0]}` : ''}
                    </span>
                    {selected === field.localId && (
                      <>
                        <button
                          type="button"
                          className="fieldbox__remove"
                          aria-label={`Remove ${field.label}`}
                          onClick={(event) => { event.stopPropagation(); removeField(field.localId); }}
                          onPointerDown={(event) => event.stopPropagation()}
                        >×</button>
                        <span
                          className="fieldbox__handle"
                          role="presentation"
                          onPointerDown={(event) => beginDrag(event, field, 'resize', size)}
                        />
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        />
      </div>
    </div>
  );
}

export function toApiFields(fields: EditorField[]) {
  return fields.map((field, index) => ({
    kind: field.kind,
    label: field.label,
    page: field.page,
    x: field.x, y: field.y, width: field.width, height: field.height,
    required: field.required,
    recipient_id: field.recipient_id || undefined,
    role_key: field.role_key || undefined,
    group_key: field.group_key || undefined,
    options: field.options || [],
    position: index,
  }));
}

export function fromApiFields(fields: any[]): EditorField[] {
  return fields.map((field) => ({
    id: field.id,
    localId: field.id || nextLocalId(),
    kind: field.kind,
    label: field.label,
    page: field.page,
    x: field.x, y: field.y, width: field.width, height: field.height,
    required: field.required,
    recipient_id: field.recipient_id ?? null,
    role_key: field.role_key ?? null,
    group_key: field.group_key ?? null,
    options: field.options || [],
  }));
}
